import "./gallery.css";

import React from "react";
import { Helmet } from "react-helmet";
import { Dialog, IconButton } from "@mui/material";
import {
  Close as CloseIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from "@mui/icons-material";

import { GALLERY } from "../../data/content";
import Reveal from "../general/Reveal";
import { PageBanner, ImageBand } from "../general/PageParts";

const IMAGE_DIR = "/images/gallery/";
const PHOTOS = GALLERY.community.images;

const photoAlt = (i) => `Community interaction and training - photo ${i + 1} of ${PHOTOS.length}`;

//Full screen photo viewer - arrow keys / buttons move between photos
const Lightbox = ({ index, onClose, onMove }) => {
  React.useEffect(() => {
    if (index === null) {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === "ArrowRight") {
        onMove(1);
      }
      else if (e.key === "ArrowLeft") {
        onMove(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index]);

  if (index === null) {
    return null;
  }
  const [file] = PHOTOS[index];

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ className: "lightbox" }}
      aria-label={"Photo viewer"}
    >
      <img
        src={`${IMAGE_DIR}${file}`}
        alt={photoAlt(index)}
      />
      <IconButton
        className={"lightbox-btn lightbox-close"}
        aria-label={"Close"}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
      <IconButton
        className={"lightbox-btn lightbox-prev"}
        aria-label={"Previous photo"}
        onClick={() => onMove(-1)}
      >
        <PrevIcon />
      </IconButton>
      <IconButton
        className={"lightbox-btn lightbox-next"}
        aria-label={"Next photo"}
        onClick={() => onMove(1)}
      >
        <NextIcon />
      </IconButton>
      <p className={"lightbox-count"}>{`${index + 1} / ${PHOTOS.length}`}</p>
    </Dialog>
  );
};

const Gallery = () => {
  const [openIndex, setOpenIndex] = React.useState(null);

  const move = (step) => setOpenIndex((i) => (i + step + PHOTOS.length) % PHOTOS.length);

  return (
    <React.Fragment>
      <Helmet>
        <title>GALLERY | PM Skillshouse Foundation</title>
      </Helmet>
      <PageBanner title={"GALLERY"} />

      <section className={"container gallery-section"}>
        <Reveal>
          <h2 className={"gallery-title"}>{GALLERY.backToSchool.title}</h2>
          <p className={"gallery-subtitle"}>{GALLERY.backToSchool.subtitle}</p>
        </Reveal>
        <Reveal
          variant={"fade"}
          className={"gallery-feature"}
        >
          <img
            src={GALLERY.backToSchool.image}
            alt={GALLERY.backToSchool.imageAlt}
            width={650}
            height={800}
          />
        </Reveal>
      </section>

      <section className={"container gallery-section"}>
        <Reveal>
          <h2 className={"gallery-title"}>{GALLERY.community.title}</h2>
          <p className={"gallery-subtitle"}>{GALLERY.community.subtitle}</p>
        </Reveal>
        <ul className={"gallery-grid"}>
          {PHOTOS.map(([file, width, height], i) => (
            <li key={file}>
              <button
                type={"button"}
                className={"gallery-thumb"}
                onClick={() => setOpenIndex(i)}
                aria-label={`Enlarge photo ${i + 1}`}
              >
                <img
                  src={`${IMAGE_DIR}${file}`}
                  alt={photoAlt(i)}
                  width={width}
                  height={height}
                  loading={"lazy"}
                />
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* Low enough that the "BEGIN." on the mug is always in view */}
      <ImageBand
        image={"/images/gallery_strip.jpg"}
        position={"center 72%"}
        fixedPosition={"center bottom"}
      />

      <Lightbox
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onMove={move}
      />
    </React.Fragment>
  );
};

export default Gallery;
